import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { can, type Me } from '../../auth/me';
import { QUOTES_RESOURCE } from '../types';
import { OfferEventsDialog } from './OfferEventsDialog';
import { formatDay, offerUrl } from './offers';
import { OFFER_STATE_LABELS, OFFERS_KEY, type OfferListRow, type OfferOf } from './types';
import './offers.css';

/** „Wyceny wydarzeń / Oferty łączone” at the top of „Wyceny” (the address keeps it: `oferty=1`). */
export function QuotesViewSwitch({ offers, onChange }: { offers: boolean; onChange: (offers: boolean) => void }) {
  return (
    <div className="segment q-view-switch" role="group" aria-label="Widok wycen">
      <button type="button" aria-pressed={!offers} onClick={() => onChange(false)}>
        Wyceny wydarzeń
      </button>
      <button type="button" aria-pressed={offers} onClick={() => onChange(true)} data-feature-id="quotes.offers">
        Oferty łączone
      </button>
    </div>
  );
}

/** The list of combined offers (owner 2026-10-30), newest first, with „+ Nowa oferta łączona”. */
export function OffersSection({ me, onSwitch }: { me: Me; onSwitch: (offers: boolean) => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const list = useQuery({ queryKey: OFFERS_KEY, queryFn: () => api<{ offers: OfferListRow[] }>('/api/quotes/offers') });
  const create = useMutation({
    mutationFn: (v: { clientId: string; eventIds: string[]; name: string }) => api<OfferOf>('/api/quotes/offers', { method: 'POST', body: v }),
    onSuccess: (of) => {
      setCreating(false);
      void queryClient.invalidateQueries({ queryKey: OFFERS_KEY });
      navigate(offerUrl(of.offer.id));
    },
  });
  const rows = list.data?.offers ?? [];
  return (
    <section className="page wide quotes-page offers-list" data-feature-id="quotes.offers">
      <div className="page-head">
        <h1>Wyceny</h1>
        {can(me, QUOTES_RESOURCE, 'create') && (
          <button className="btn primary" type="button" onClick={() => setCreating(true)}>
            + Nowa oferta łączona
          </button>
        )}
      </div>
      <QuotesViewSwitch offers onChange={onSwitch} />
      <p className="lede">Oferta łączona zbiera kilka wydarzeń jednego klienta: każde z datami i swoją kwotą, na końcu suma, osobny rabat na sprzęt i osobny na ludzi.</p>
      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać ofert'}</p>}
      {!list.isLoading && rows.length === 0 && <p className="muted">Nie ma jeszcze ofert łączonych. Zacznij od „+ Nowa oferta łączona” albo zaznacz w kalendarzu kilka wydarzeń klienta (⌘-klik) i wybierz „Połącz w ofertę”.</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table" aria-label="Oferty łączone">
            <thead>
              <tr>
                <th scope="col">Oferta</th>
                <th scope="col">Klient</th>
                <th scope="col" className="num">
                  Wydarzenia
                </th>
                <th scope="col">Od</th>
                <th scope="col">Stan</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <th scope="row">
                    <Link to={offerUrl(r.id)}>{r.name || `Oferta dla: ${r.clientName}`}</Link>
                  </th>
                  <td>{r.clientName}</td>
                  <td className="num">{r.eventCount}</td>
                  <td>{r.firstStartsAt ? formatDay(r.firstStartsAt) : '—'}</td>
                  <td>
                    <span className="q-vnum">v{r.number}</span>{' '}
                    <span className="q-vstate" data-state={r.state}>
                      {OFFER_STATE_LABELS[r.state]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && (
        <OfferEventsDialog
          clientId={null}
          chosen={[]}
          title="Nowa oferta łączona"
          submitLabel="Utwórz ofertę"
          withName
          busy={create.isPending}
          error={create.error instanceof ApiError ? create.error.message : create.error ? 'Nie udało się utworzyć oferty' : null}
          onSave={(eventIds, clientId, name) => create.mutate({ clientId, eventIds, name })}
          onClose={() => setCreating(false)}
        />
      )}
    </section>
  );
}
